import React, { createContext, useContext, useState, forwardRef } from 'react';

import { ChevronDown } from '@pray/shared/components/ui/Icons/ChevronDown';
import { cn } from '@pray/shared/utils/styles';

// --- Context Definition ---
type AccordionContextType = {
  value: string[];
  setValue: React.Dispatch<React.SetStateAction<string[]>>;
  type: 'single' | 'multiple';
};

const AccordionContext = createContext<AccordionContextType | null>(null);

const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('Accordion components must be used within an <Accordion> component.');
  }
  return context;
};

type AccordionItemContextType = {
  value: string;
  isOpen: boolean;
};

const AccordionItemContext = createContext<AccordionItemContextType | null>(null);

const useAccordionItemContext = () => {
  const context = useContext(AccordionItemContext);
  if (!context) {
    throw new Error('AccordionItem components must be used within an <AccordionItem> component.');
  }
  return context;
};

// --- Accordion (Root Component) ---
type AccordionProps = React.ComponentPropsWithoutRef<'div'> & {
  type?: 'single' | 'multiple';
  defaultValue?: string | string[];
};

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  ({ children, className, type = 'single', defaultValue, ...props }, ref) => {
    const getInitialValue = () => {
      if (!defaultValue) return [];
      if (Array.isArray(defaultValue)) return defaultValue;
      return [defaultValue];
    };
    const [value, setValue] = useState<string[]>(getInitialValue);

    const contextValue = React.useMemo(() => ({ value, setValue, type }), [value, setValue, type]);

    return (
      <AccordionContext.Provider value={contextValue}>
        <div ref={ref} className={cn('divide-y divide-[#5D5F65]', className)} {...props}>
          {children}
        </div>
      </AccordionContext.Provider>
    );
  }
);
Accordion.displayName = 'Accordion';

// --- AccordionItem Component ---
type AccordionItemProps = React.ComponentPropsWithoutRef<'div'> & {
  value: string;
};

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, className, value, ...props }, ref) => {
    const { value: contextValue } = useAccordionContext();
    const isOpen = contextValue.includes(value);

    const itemContextValue = React.useMemo(() => ({ value, isOpen }), [value, isOpen]);

    return (
      <AccordionItemContext.Provider value={itemContextValue}>
        <div ref={ref} data-state={isOpen ? 'open' : 'closed'} className={className} {...props}>
          {children}
        </div>
      </AccordionItemContext.Provider>
    );
  }
);
AccordionItem.displayName = 'AccordionItem';

// --- AccordionTrigger Component ---
type AccordionTriggerProps = React.ComponentPropsWithoutRef<'button'>;

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, ...props }, ref) => {
    const { setValue, type } = useAccordionContext();
    const { value: itemValue, isOpen } = useAccordionItemContext();

    const handleClick = () => {
      setValue((prevValue) => {
        if (type === 'single') {
          return prevValue.includes(itemValue) ? [] : [itemValue];
        }
        // multiple
        return prevValue.includes(itemValue) ? prevValue.filter((v) => v !== itemValue) : [...prevValue, itemValue];
      });
    };

    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        data-state={isOpen ? 'open' : 'closed'}
        aria-expanded={isOpen}
        // Consider adding aria-controls pointing to the AccordionContent ID if needed
        className={cn(
          'flex w-full flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180', // Base styles similar to shadcn
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown className="size-4 shrink-0 transition-transform duration-200" />
      </button>
    );
  }
);
AccordionTrigger.displayName = 'AccordionTrigger';

// --- AccordionContent Component ---
type AccordionContentProps = React.ComponentPropsWithoutRef<'div'>;

const AccordionContent = forwardRef<HTMLDivElement, AccordionContentProps>(({ children, className, ...props }, ref) => {
  const { isOpen } = useAccordionItemContext();

  return (
    <div
      ref={ref}
      data-state={isOpen ? 'open' : 'closed'}
      hidden={!isOpen} // Simple hide/show, or use animation classes
      className={cn(
        'grid overflow-hidden transition-all duration-300 ease-in-out',
        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        className
      )}
      {...props}
    >
      {/* Add padding or other styles as needed, often applied here */}
      <div className={cn('overflow-hidden transition-all duration-300 ease-in-out', isOpen && 'pb-4')}>{children}</div>
    </div>
  );
});
AccordionContent.displayName = 'AccordionContent';

// --- Exports ---
export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
